import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Search, Edit, Trash2, Plus, MapPin, Phone, 
  DollarSign, ToggleLeft, ToggleRight, Building2, Calendar, Eye, Check, X
} from 'lucide-react';
import { cities, getDistricts } from '@/data/turkeyData';
import AddFacilityForm from '@/components/AddFacilityForm';
import { FACILITY_TYPES, getFacilityTypeLabel } from '@/data/facilityTypes';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const FacilitiesPage = () => {
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Add dialog (uses shared AddFacilityForm)
  const [showAddDialog, setShowAddDialog] = useState(false);
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingFacility, setDeletingFacility] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Bookings dialog
  const [showBookingsDialog, setShowBookingsDialog] = useState(false);
  const [facilityBookings, setFacilityBookings] = useState([]);
  const [selectedFacilityForBookings, setSelectedFacilityForBookings] = useState(null);
  const [loadingBookings, setLoadingBookings] = useState(false);

  useEffect(() => {
    fetchFacilities();
  }, []);

  useEffect(() => {
    filterFacilities();
  }, [facilities, searchTerm, cityFilter, typeFilter, partnerFilter, statusFilter]);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get(`${API}/admin/facilities`, { withCredentials: true });
      setFacilities(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Fetch facilities error:', error);
      toast.error('Tesisler yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  const filterFacilities = () => {
    let filtered = [...facilities];

    // Search
    if (searchTerm) {
      filtered = filtered.filter(f => 
        f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.district?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // City filter
    if (cityFilter !== 'all') {
      filtered = filtered.filter(f => f.city === cityFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(f => f.facility_type === typeFilter);
    }

    // Partner filter
    if (partnerFilter !== 'all') {
      const isPartner = partnerFilter === 'partner';
      filtered = filtered.filter(f => f.is_partner === isPartner);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(f => f.status === statusFilter);
    }

    setFilteredFacilities(filtered);
  };

  const handleAdd = () => {
    setShowAddDialog(true);
  };

  const handleEdit = (facility) => {
    setEditingFacility(facility);
    setEditForm({
      name: facility.name || '',
      city: facility.city || '',
      district: facility.district || '',
      address: facility.address || '',
      phone: facility.phone || '',
      price_per_hour: facility.price_per_hour || '',
      description: facility.description || '',
      facility_type: facility.facility_type || 'hali_saha',
      is_partner: facility.is_partner || false,
    });
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    if (!editForm.name || !editForm.city || !editForm.district || !editForm.address || !editForm.price_per_hour) {
      toast.error('Lütfen zorunlu alanları doldurun (Ad, Şehir, İlçe, Adres, Fiyat)');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/facilities/${editingFacility.facility_id}`,
        editForm,
        { withCredentials: true }
      );
      toast.success('Tesis güncellendi');
      setShowEditDialog(false);
      fetchFacilities();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (facility) => {
    setDeletingFacility(facility);
    setShowDeleteDialog(true);
  };
  
  const handleViewBookings = async (facility) => {
    setSelectedFacilityForBookings(facility);
    setShowBookingsDialog(true);
    setLoadingBookings(true);
    
    try {
      const response = await axios.get(`${API}/admin/bookings`, { withCredentials: true });
      const facilityBookings = response.data.filter(b => b.facility_id === facility.facility_id);
      setFacilityBookings(facilityBookings);
    } catch (error) {
      console.error('Fetch bookings error:', error);
      toast.error('Rezervasyonlar yüklenemedi');
    } finally {
      setLoadingBookings(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/admin/facilities/${deletingFacility.facility_id}`,
        { withCredentials: true }
      );
      toast.success('Tesis silindi');
      setShowDeleteDialog(false);
      fetchFacilities();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    } finally {
      setDeleting(false);
    }
  };

  const togglePartner = async (facility) => {
    try {
      await axios.post(
        `${API}/admin/facilities/${facility.facility_id}/toggle-partner`,
        {},
        { withCredentials: true }
      );
      toast.success('Partner durumu güncellendi');
      fetchFacilities();
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('İşlem başarısız');
    }
  };

  const handleApprove = async (facility) => {
    try {
      await axios.post(
        `${API}/admin/facilities/${facility.facility_id}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success('Tesis onaylandı');
      fetchFacilities();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error(error.response?.data?.detail || 'Onaylama başarısız');
    }
  };

  const handleReject = async (facility) => {
    try {
      await axios.post(
        `${API}/admin/facilities/${facility.facility_id}/reject`,
        {},
        { withCredentials: true }
      );
      toast.success('Tesis reddedildi');
      fetchFacilities();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error(error.response?.data?.detail || 'Reddetme başarısız');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tesisler</h1>
          <p className="text-sm text-slate-600 mt-1">
            Toplam {facilities.length} tesis • Görüntülenen {filteredFacilities.length}
          </p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleAdd} data-testid="admin-add-facility-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Tesis Ekle
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Tesis ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* City Filter */}
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Şehir" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Şehirler</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city.id} value={city.name}>{city.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Tür" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Türler</SelectItem>
                {FACILITY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="pending">Onay Bekliyor</SelectItem>
                <SelectItem value="approved">Onaylı</SelectItem>
                <SelectItem value="rejected">Reddedilmiş</SelectItem>
              </SelectContent>
            </Select>

            {/* Partner Filter */}
            <Select value={partnerFilter} onValueChange={setPartnerFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Partner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="partner">Sadece Partner</SelectItem>
                <SelectItem value="non-partner">Partner Değil</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tesis Adı</TableHead>
                <TableHead>Şehir/İlçe</TableHead>
                <TableHead>Tür</TableHead>
                <TableHead>Fiyat</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Telefon</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFacilities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    Tesis bulunamadı
                  </TableCell>
                </TableRow>
              ) : (
                filteredFacilities.map((facility) => {
                  const statusConfig = {
                    pending: { label: 'Onay Bekliyor', color: 'bg-orange-100 text-orange-700' },
                    approved: { label: 'Onaylı', color: 'bg-green-100 text-green-700' },
                    rejected: { label: 'Reddedilmiş', color: 'bg-red-100 text-red-700' }
                  };
                  const status = statusConfig[facility.status] || statusConfig.pending;

                  return (
                    <TableRow key={facility.facility_id}>
                      <TableCell className="font-medium">{facility.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <MapPin className="w-3 h-3" />
                          {facility.city}, {facility.district}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getFacilityTypeLabel(facility.facility_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm font-medium">
                          <DollarSign className="w-3 h-3" />
                          {facility.price_per_hour} ₺/saat
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={status.color}>{status.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePartner(facility)}
                          className="gap-1"
                        >
                          {facility.is_partner ? (
                            <>
                              <ToggleRight className="w-4 h-4 text-emerald-600" />
                              <span className="text-emerald-600 text-xs">Aktif</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-400 text-xs">Pasif</span>
                            </>
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {facility.phone ? (
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Phone className="w-3 h-3" />
                            {facility.phone}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {facility.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApprove(facility)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Onayla"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReject(facility)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Reddet"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewBookings(facility)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Rezervasyonları Görüntüle"
                          >
                            <Calendar className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(facility)}
                            title="Düzenle"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(facility)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tesis Düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Tesis Adı</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                />
              </div>

              <div>
                <Label>Şehir</Label>
                <Select value={editForm.city} onValueChange={(value) => setEditForm({...editForm, city: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map(city => (
                      <SelectItem key={city.id} value={city.name}>{city.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>İlçe</Label>
                <Input
                  value={editForm.district}
                  onChange={(e) => setEditForm({...editForm, district: e.target.value})}
                />
              </div>

              <div className="col-span-2">
                <Label>Adres</Label>
                <Input
                  value={editForm.address}
                  onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                />
              </div>

              <div>
                <Label>Telefon</Label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  placeholder="5551234567"
                />
              </div>

              <div>
                <Label>Saat Başı Fiyat (₺)</Label>
                <Input
                  type="number"
                  value={editForm.price_per_hour}
                  onChange={(e) => setEditForm({...editForm, price_per_hour: Number(e.target.value)})}
                />
              </div>

              <div>
                <Label>Tür</Label>
                <Select value={editForm.facility_type} onValueChange={(value) => setEditForm({...editForm, facility_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FACILITY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Partner Durumu</Label>
                <Select value={editForm.is_partner ? 'true' : 'false'} onValueChange={(value) => setEditForm({...editForm, is_partner: value === 'true'})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Partner</SelectItem>
                    <SelectItem value="false">Partner Değil</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>Açıklama</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={4}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="admin-facility-save-btn">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Facility Dialog - uses same form as Operator */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni Tesis Ekle</DialogTitle>
          </DialogHeader>
          <AddFacilityForm
            endpoint={`${API}/admin/facilities`}
            onSuccess={() => {
              setShowAddDialog(false);
              fetchFacilities();
            }}
            onCancel={() => setShowAddDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tesis Sil</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              <strong>{deletingFacility?.name}</strong> tesisini silmek istediğinize emin misiniz?
            </p>
            <p className="text-sm text-red-600 mt-2">
              ⚠️ Bu işlem geri alınamaz. Tesis ile ilgili tüm rezervasyonlar, yorumlar ve veriler silinecektir.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              İptal
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Siliniyor...' : 'Evet, Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bookings Dialog */}
      <Dialog open={showBookingsDialog} onOpenChange={setShowBookingsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedFacilityForBookings?.name} - Rezervasyonlar
            </DialogTitle>
          </DialogHeader>
          
          {loadingBookings ? (
            <div className="py-8 text-center">
              <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <div className="py-4">
              {facilityBookings.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Bu tesis için rezervasyon yok</p>
              ) : (
                <div className="space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">{facilityBookings.length}</p>
                      <p className="text-xs text-slate-600">Toplam Rezervasyon</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">
                        {facilityBookings.filter(b => b.payment_status === 'CONFIRMED').length}
                      </p>
                      <p className="text-xs text-green-600">Onaylı</p>
                    </div>
                    <div className="text-center p-3 bg-emerald-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-700">
                        {facilityBookings
                          .filter(b => b.payment_status === 'CONFIRMED')
                          .reduce((sum, b) => sum + (b.brut_tutar || 0), 0)
                          .toLocaleString('tr-TR')} ₺
                      </p>
                      <p className="text-xs text-emerald-600">Toplam Gelir</p>
                    </div>
                  </div>
                  
                  {/* Bookings Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Rezervasyon Kodu</TableHead>
                          <TableHead>Kullanıcı</TableHead>
                          <TableHead>Tarih</TableHead>
                          <TableHead>Saat</TableHead>
                          <TableHead>Tutar</TableHead>
                          <TableHead>Durum</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {facilityBookings.map(booking => {
                          const statusConfig = {
                            CONFIRMED: { label: 'Onaylandı', color: 'bg-green-100 text-green-700' },
                            PENDING_PAYMENT: { label: 'Bekliyor', color: 'bg-orange-100 text-orange-700' },
                            CANCELLED: { label: 'İptal', color: 'bg-red-100 text-red-700' },
                            EXPIRED: { label: 'Doldu', color: 'bg-slate-100 text-slate-700' }
                          };
                          const status = statusConfig[booking.payment_status] || statusConfig.PENDING_PAYMENT;
                          
                          return (
                            <TableRow key={booking.booking_id}>
                              <TableCell className="font-mono text-xs">{booking.booking_code}</TableCell>
                              <TableCell className="text-sm">{booking.user_name}</TableCell>
                              <TableCell className="text-sm">{booking.date}</TableCell>
                              <TableCell className="text-sm">{booking.start_time} - {booking.end_time}</TableCell>
                              <TableCell className="font-medium">{booking.brut_tutar} ₺</TableCell>
                              <TableCell>
                                <Badge className={status.color}>{status.label}</Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingsDialog(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilitiesPage;
